import * as React from 'react'

import { SearchIcon, SettingsIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { LocationType } from '~/client/graph'
import DateSelector from '~/client/src/desktop/components/Filters/DateSelector/DateSelector'
import ColumnsConfigurationMenu from '~/client/src/desktop/components/HeaderBar/components/ColumnsConfigurationMenu'
import ReportModal from '~/client/src/desktop/components/HeaderBar/components/ReportModal'
import BaseHeaderBar from '~/client/src/desktop/components/HeaderBar/HeaderBar'
import PopoverComponent from '~/client/src/desktop/components/PopoverComponent/PopoverComponent'
import type { SavedViewsRef } from '~/client/src/desktop/components/SavedViews'
import { TwoMonthsDatePickerMode } from '~/client/src/desktop/components/TwoMonthsDatePicker/TwoMonthsDatePicker.store'
import LogisticActionTypes, {
  getLogisticActionTypeDisplayName,
} from '~/client/src/desktop/enums/LogisticsActionTypes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import { CollapseButton } from '~/client/src/shared/components/CollapseButton/CollapseButton'
import DynamicOverflowListStore from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList.store'
import FacetButton from '~/client/src/shared/components/Filters/components/FacetButton/FacetButton'
import EntityNameFilter from '~/client/src/shared/components/Filters/EntityNameFilter/EntityNameFilter'
import { ClearDayIcon } from '~/client/src/shared/components/icons/ClearDayIcon'
import { DeleteIcon } from '~/client/src/shared/components/icons/DeleteIcon'
import { DuplicateIcon } from '~/client/src/shared/components/icons/DuplicateIcon'
import { StarUnfilledIcon } from '~/client/src/shared/components/icons/StarUnfilledIcon'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import LogisticsCreationListMenu from '~/client/src/shared/components/SitemapCards/LogisticsCreationListMenu'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import { buildFilterTypeIconMap } from '~/client/src/shared/constants/FilterTypeMappings'
import locationTypeToTagIconMap from '~/client/src/shared/constants/locationTypeToTagIconMap'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import { LogisticsFilterType } from '~/client/src/shared/enums/LogisticsFilterType'
import LogisticsGroupingOption, {
  formsGroupingOptionList,
  getLogisticsGroupingOptionTranslatorKey,
} from '~/client/src/shared/enums/LogisticsGroupingOption'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProject from '~/client/src/shared/models/UserProject'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { RESET_ALL_FILTERS } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventContext from '~/client/src/shared/stores/EventStore/EventContext'
import LogisticsFilterStore from '~/client/src/shared/stores/LogisticsFilter.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { getLocationTypeDisplayName } from '~/client/src/shared/types/IHierarchyParent'
import { LogisticsDataKeys } from '~/client/src/shared/types/LogisticsDataKeys'
import {
  getLocationFilterKeySummary,
  ILocationFilterKeySummary,
} from '~/client/src/shared/utils/logisticsHelper'
import { IStatusItem } from '~/client/src/shared/utils/permitStatusUtils'

import ConfirmBatchStatusUpdateDialog from '../ConfirmBatchStatusUpdateDialog'
import LogisticsStore from '../Logistics/Logistics.store'
import LogisticsGanttStore from '../LogisticsGantt/LogisticsGantt.store'
import LogisticsListStore from '../LogisticsList/LogisticsList.store'
import LogisticsListFacetStore from '../LogisticsList/LogisticsListFacet.store'
import StatusUpdateMenu from '../StatusUpdateMenu'
import SubscribeUsersSelector from '../SubscribeUsersSelector/SubscribeUsersSelector'
import LogisticsFilter from './components/BaseLogisticsFilters/LogisticsFilter'
import LogisticsGroupingOptions from './components/LogisticsGroupingOptions/LogisticsGroupingOptions'
import LogisticsGroupingOptionsStore from './components/LogisticsGroupingOptions/LogisticsGroupingOptions.store'
import LogisticsListFilterStore from './components/LogisticsListFilter/LogisticsListFilter.store'
import LogisticsSortingOptions from './components/LogisticsSortingOptions/LogisticsSortingOptions'
import LogisticsSortingOptionsStore from './components/LogisticsSortingOptions/LogisticsSortingOptions.store'
import PermitReportButton from './components/PermitReportButton'
import WorkflowSavedViews from './components/WorkflowSavedViews/WorkflowSavedViews'
import LogisticsHeaderBarStore from './LogisticsHeaderBar.store'

interface IProps {
  isMapViewDisabled: boolean
  logisticsStore: LogisticsStore
  logisticsListStore: LogisticsListStore
  logisticsGanttStore: LogisticsGanttStore
  projectsStore: ProjectsStore
  projectDateStore: ProjectDateStore

  eventsStore?: DesktopEventStore
  locationAttributesStore?: LocationAttributesStore
  workflowTypesStore?: WorkflowTypesStore
  companiesStore?: CompaniesStore
  tagsStore?: TagsStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  state?: DesktopInitialState
  logisticsListFacetStore: LogisticsListFacetStore
  userProjectsStore?: UserProjectsStore
  sharedSitePermitStore?: SharedSitePermitStore
}

const specificFilterTypes: string[] = ['locations']

type IAction = {
  actionType: LogisticActionTypes
  icon: any
  isShown(isAdminMode?: boolean): boolean
}

const secondaryActions: IAction[] = [
  {
    actionType: LogisticActionTypes.SUBSCRIBE,
    icon: <StarUnfilledIcon size="sm" data-icon="inline-start" />,
    isShown: () => true,
  },
  {
    actionType: LogisticActionTypes.UPDATE_STATUS,
    icon: <ClearDayIcon size="sm" data-icon="inline-start" />,
    isShown: isAdminMode => isAdminMode,
  },
  {
    actionType: LogisticActionTypes.DUPLICATE,
    icon: <DuplicateIcon size="sm" data-icon="inline-start" />,
    isShown: () => true,
  },
  {
    actionType: LogisticActionTypes.DELETE,
    icon: <DeleteIcon size="sm" data-icon="inline-start" />,
    isShown: isAdminMode => isAdminMode,
  },
]

const isDeleteAction = (actionType: LogisticActionTypes): boolean =>
  actionType === LogisticActionTypes.DELETE

const isDuplicateAction = (actionType: LogisticActionTypes): boolean =>
  actionType === LogisticActionTypes.DUPLICATE

const isUpdateStatusAction = (actionType: LogisticActionTypes): boolean =>
  actionType === LogisticActionTypes.UPDATE_STATUS

const isSubscribeAction = (actionType: LogisticActionTypes): boolean =>
  actionType === LogisticActionTypes.SUBSCRIBE

const ICON_SIZE = 16
const FILTER_TYPE_ICONS = buildFilterTypeIconMap({
  size: ICON_SIZE,
  className: 'tw:shrink-0 tw:mr-2.5',
})

@inject(
  'state',
  'eventsStore',
  'locationAttributesStore',
  'workflowTypesStore',
  'companiesStore',
  'tagsStore',
  'userProjectsStore',
  'workflowCategoriesStore',
  'sharedSitePermitStore',
)
@observer
export default class LogisticsHeaderBar extends BaseHeaderBar<IProps> {
  @observable private localProps: IProps = null
  @observable private isColumnsMenuShown: boolean = false
  @observable private columnSearchKey: string = ''
  protected store: LogisticsHeaderBarStore = null
  private readonly logisticsFilterStore: LogisticsListFilterStore = null
  private readonly logisticsGroupingOptionsStore: LogisticsGroupingOptionsStore
  private readonly dynamicOverflowListStore: DynamicOverflowListStore = null
  private clearPostEventCallback: () => void
  private workflowSavedViewsRef = React.createRef<SavedViewsRef>()

  public constructor(props: IProps) {
    super(props)

    this.dynamicOverflowListStore = new DynamicOverflowListStore()
    this.localProps = props
    const { eventsStore } = props

    this.store = new LogisticsHeaderBarStore(
      props.eventsStore,
      props.logisticsStore,
      props.isMapViewDisabled,
    )

    const { onShowGroupByChanged, onShowChanged } = this.store

    this.logisticsFilterStore = new LogisticsListFilterStore(
      props.eventsStore,
      props.logisticsStore,
      props.logisticsListStore,
      onShowChanged,
      props.locationAttributesStore,
      props.workflowTypesStore,
      props.tagsStore,
      props.companiesStore,
      props.workflowCategoriesStore,
      props.projectsStore,
      props.sharedSitePermitStore,
      props.logisticsListFacetStore,
      this.dynamicOverflowListStore.enableRecalculation,
    )
    this.logisticsGroupingOptionsStore = new LogisticsGroupingOptionsStore(
      eventsStore,
      onShowGroupByChanged,
      this.onGroupingChanged,
      this.appState.workflowsFilters,
      formsGroupingOptionList,
      LogisticsGroupingOption.TYPE,
      getLogisticsGroupingOptionTranslatorKey,
    )

    makeObservable(this)
  }

  public componentDidMount() {
    this.store.setInitialDateValue()
    this.store.loadReportTemplate()

    this.clearPostEventCallback = this.props.eventsStore.addPostEventCallback(
      this.onFiltersReset,
    )
  }

  public componentWillUnmount() {
    this.clearPostEventCallback()
  }

  private onFiltersReset = (eventContext: EventContext) => {
    const [eventType] = eventContext.event

    switch (eventType) {
      case RESET_ALL_FILTERS:
        return Object.values(
          this.logisticsFilterStore.filterStoresByTypeMap,
        ).forEach(filterStore => {
          filterStore.clickOnDeselectAll()
          filterStore.clickOnApply()
        })
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (this.props.isMapViewDisabled !== prevProps.isMapViewDisabled) {
      this.store.isMapViewDisabled = !this.store.isMapViewDisabled
    }
  }

  private get groupedBy() {
    const { groupingKey } = this.props.logisticsListStore
    return Localization.translator[
      getLogisticsGroupingOptionTranslatorKey(groupingKey)
    ]
  }

  private get isAdminUser() {
    return this.props.state.userActiveProjectSettings?.isSuperUser
  }

  protected renderModal() {
    const { startDate, endDate, isProjectCalendarModeActive } =
      this.props.logisticsStore
    const { additionalReportFiles, toggleAdditionalReportFiles } = this.store
    const { filterStoresByTypeMap } = this.logisticsFilterStore

    return (
      <ReportModal
        startDate={startDate}
        endDate={endDate}
        groupedBy={this.groupedBy}
        toggleModal={this.toggleModal}
        getIconByFilter={this.getIconByFilter}
        filterStoresByTypeMap={filterStoresByTypeMap}
        additionalReportFiles={additionalReportFiles}
        toggleAdditionalReportFiles={toggleAdditionalReportFiles}
        renderButton={this.renderReportButton}
        handleSpicificFilterType={this.handleSpecificFilterType}
        specificFilterTypes={specificFilterTypes}
        reportName={Localization.translator.formReport}
        isFullReport={isProjectCalendarModeActive}
      />
    )
  }

  protected renderLeftSection() {
    return (
      <div className="tw:flex tw:items-center tw:justify-start tw:h-full left-bar tw:grow-0!" />
    )
  }

  protected renderCenterSection(): JSX.Element {
    return (
      <WorkflowSavedViews
        ref={this.workflowSavedViewsRef}
        logisticsFilterStore={this.logisticsFilterStore}
        filters={this.appState.workflowsFilters}
        projectId={this.props.state?.activeProject?.id}
      />
    )
  }

  protected renderRightSection() {
    const { logisticsStore, logisticsListStore } = this.props
    const { openFormTypeSelectionForm, isCreationAvailable } = logisticsStore

    return (
      <div className="tw:flex tw:items-center tw:gap-4">
        <div className="tw:flex tw:items-center tw:h-full tw:grow-0 tw:border-0 tw:border-l tw:border-solid tw:border-border">
          {this.renderDatePicker()}
          {isCreationAvailable && this.store.isCalendarViewMode && (
            <div className="tw:grow-0 tw:relative">
              <LogisticsCreationListMenu
                openFormTypeSelectionForm={openFormTypeSelectionForm}
                className="tw:ml-[15px]"
              />
            </div>
          )}
        </div>
        {this.store.isListViewMode && (
          <PermitReportButton
            title={Localization.translator.createReport}
            logisticsStore={logisticsStore}
            logisticsListStore={logisticsListStore}
            toggleModal={this.toggleModal}
            shouldPreventCreation={true}
            getLocationFilterKeySummary={this.getLocationFilterKeySummary}
          />
        )}
      </div>
    )
  }

  @action.bound
  private toggleColumnsMenu() {
    this.isColumnsMenuShown = !this.isColumnsMenuShown
  }

  @action.bound
  private changeColumnSearchKey(newSearchKey: string) {
    this.columnSearchKey = newSearchKey
  }

  private get excludedFromSortKeys(): string[] {
    return [LogisticsDataKeys.CHECKBOX]
  }

  private renderColumnsConfiguration = () => {
    if (!this.props.state.userActiveProjectSettings?.isAdmin) {
      return null
    }

    const targetStore = this.store.isGanttViewMode
      ? this.props.logisticsGanttStore
      : this.props.logisticsListStore

    const {
      columns,
      updateColumnsConfiguration,
      resetColumnsConfiguration,
      isConfigurationLoading,
    } = targetStore

    return (
      <div className="tw:grow-0">
        {this.isColumnsMenuShown && (
          <MenuCloser
            closeMenu={this.toggleColumnsMenu}
            className="tw:flex tw:items-center tw:justify-end tw:relative"
          >
            <ColumnsConfigurationMenu
              columns={columns.filter(
                c => c.dataKey !== LogisticsDataKeys.LOGISTICS_TIMELINE,
              )}
              restoreDefaults={resetColumnsConfiguration}
              searchKey={this.columnSearchKey}
              changeSearchKey={this.changeColumnSearchKey}
              updateColumnsConfiguration={updateColumnsConfiguration}
              excludedFromSort={this.excludedFromSortKeys}
              isLoading={isConfigurationLoading}
              className="fixed-right"
            />
          </MenuCloser>
        )}
      </div>
    )
  }

  private renderSortingOptions() {
    const { isGanttViewMode, isListViewMode, isMapViewMode } = this.store

    if (!isListViewMode && !isGanttViewMode && !isMapViewMode) {
      return null
    }

    const targetStore = isGanttViewMode
      ? this.props.logisticsGanttStore
      : this.props.logisticsListStore

    const columnKeyMap = isGanttViewMode
      ? {
          [LogisticsDataKeys.LOGISTIC_DATE]:
            LogisticsDataKeys.LOGISTIC_START_DATE,
        }
      : undefined

    const sortingStore = new LogisticsSortingOptionsStore(
      targetStore,
      columnKeyMap,
    )

    return (
      <>
        <div className="tw:grow-0">
          <LogisticsSortingOptions
            store={sortingStore}
            forceClose={this.store.forceSortByClose}
          />
        </div>
        <div className="separator tw:mx-2.5 tw:grow-0" />
      </>
    )
  }

  private isOnlyFilterSelected = (
    filtersStore: LogisticsFilterStore,
    filterType: LogisticsFilterType,
    id: string,
  ) => {
    const filter = filtersStore.filterStoresByTypeMap[filterType]
    return filter.selectedOptions?.size === 1 && filter.selectedOptions.has(id)
  }

  private toggleFilterOnly = (
    filtersStore: LogisticsFilterStore,
    filterType: LogisticsFilterType,
    id?: string,
  ) => {
    if (!id) {
      return
    }
    const filterStore = filtersStore.filterStoresByTypeMap[filterType]
    if (this.isOnlyFilterSelected(filtersStore, filterType, id)) {
      filterStore.clickOnDeselectAll()
    } else {
      filterStore.selectOptionsByIds([id])
    }
    filterStore.clickOnApply()
  }

  private renderMyEntitiesButton = () => {
    const myId = this.props.state.user?.id
    const myAvatar = this.props.state.user.avatarUrl

    return (
      <FacetButton
        className="tw:!grow-0 tw:!shrink-0 tw:!basis-auto"
        icon={
          <Avatar
            src={myAvatar}
            avatarType={AvatarType.User}
            size={AvatarSize.Minuscule}
          />
        }
        label={Localization.translator.me}
        isActive={this.isOnlyFilterSelected(
          this.logisticsFilterStore,
          LogisticsFilterType.ResponsibleContact,
          myId,
        )}
        onClick={() =>
          this.toggleFilterOnly(
            this.logisticsFilterStore,
            LogisticsFilterType.ResponsibleContact,
            myId,
          )
        }
        showChevron={false}
      />
    )
  }

  private renderMyCompanyButton = () => {
    const {
      userProjectsStore,
      companiesStore,
      state: { user },
    } = this.props

    const myCompanyId = UserProject.getCompanyId(user, userProjectsStore)
    const myCompany = companiesStore.getCompanyById(myCompanyId)
    return (
      <FacetButton
        className="tw:!grow-0 tw:!shrink-0 tw:!basis-auto"
        icon={
          <Avatar
            src={myCompany?.avatarUrl}
            avatarType={AvatarType.Company}
            size={AvatarSize.Minuscule}
          />
        }
        label={Localization.translator.myCo}
        isActive={this.isOnlyFilterSelected(
          this.logisticsFilterStore,
          LogisticsFilterType.Company,
          myCompanyId,
        )}
        onClick={() =>
          this.toggleFilterOnly(
            this.logisticsFilterStore,
            LogisticsFilterType.Company,
            myCompanyId,
          )
        }
        showChevron={false}
      />
    )
  }

  private renderSearchFilter() {
    const { forceSearchClose, onShowSearchChanged } = this.store

    return (
      <EntityNameFilter
        filters={this.appState.workflowsFilters}
        forceClose={forceSearchClose}
        onShowChanged={onShowSearchChanged}
        icon={
          <SearchIcon
            size={ICON_SIZE}
            className="tw:shrink-0 tw:ml-1.5 tw:mr-1"
          />
        }
        shouldShowBaseText
        testId="forms-search-input"
      />
    )
  }

  private renderGroupingOptions() {
    const {
      forceGroupByClose,
      isListViewMode,
      isGanttViewMode,
      isMapViewMode,
    } = this.store

    if (!isListViewMode && !isGanttViewMode && !isMapViewMode) {
      return null
    }

    return (
      <>
        <div className="tw:grow-0">
          <LogisticsGroupingOptions
            store={this.logisticsGroupingOptionsStore}
            forceClose={forceGroupByClose}
          />
        </div>
        <div className="separator tw:mx-2.5 tw:grow-0" />
      </>
    )
  }

  protected renderActionBar(): JSX.Element {
    const { isGanttViewMode, isListViewMode } = this.store

    if (!isListViewMode && !isGanttViewMode) {
      return null
    }

    const targetStore = isGanttViewMode
      ? this.props.logisticsGanttStore
      : this.props.logisticsListStore

    const {
      isSomeRowSelected,
      statusTitle,
      collapseAllCategories,
      expandAllCategories,
      isAllCollapsed,
    } = targetStore

    const isAllSelected =
      targetStore.selectedInstancesIds.length ===
      targetStore.filteredCollection.length
    const isSomeButNotAll = isSomeRowSelected && !isAllSelected

    return (
      <div className="tw:flex tw:items-center tw:w-full tw:bg-background tw:border-b tw:border-border tw:py-1.5 tw:pl-3.5 tw:gap-2 tw:min-h-[45px]">
        <Checkbox
          checked={isAllSelected}
          indeterminate={isSomeButNotAll}
          onCheckedChange={() => this.toggleSelectAll()}
        />

        {!isAllCollapsed && this.renderCollapseBtn(collapseAllCategories)}
        {isAllCollapsed && this.renderCollapseBtn(expandAllCategories, true)}

        <span className="tw:text-sm tw:font-medium tw:text-muted-foreground tw:whitespace-nowrap">
          {statusTitle}
        </span>

        {isSomeRowSelected && (
          <>
            <div className="tw:flex tw:items-center tw:h-8 tw:py-0.5 tw:flex-shrink-0">
              <div className="tw:w-px tw:h-full tw:bg-border" />
            </div>
            {secondaryActions.map(this.renderActionButton)}
          </>
        )}

        <div className="tw:flex-1" />

        <div className="tw:flex tw:items-center">
          <div
            className={classList({
              'tw:cursor-pointer tw:grow-0 tw:flex tw:items-center': true,
              'tw:text-primary tw:pointer-events-none': this.isColumnsMenuShown,
            })}
            onClick={this.toggleColumnsMenu}
            title={Localization.translator.configureTableColumns}
          >
            <SettingsIcon
              size={ICON_SIZE}
              className={classList({
                'tw:shrink-0 tw:mx-2.5 tw:rounded-full': true,
                'tw:bg-primary/10': this.isColumnsMenuShown,
              })}
            />
          </div>
          {this.renderColumnsConfiguration()}
        </div>
      </div>
    )
  }

  private renderCollapseBtn = (onClick: () => void, isExpand?: boolean) => {
    const isExpanded = !isExpand
    return (
      <CollapseButton
        isExpanded={isExpanded}
        onClick={onClick}
        ariaLabel={
          isExpanded
            ? Localization.translator.collapseAll
            : Localization.translator.expandAll
        }
      />
    )
  }

  private toggleSelectAll = () => {
    const store = this.selectedLogisticsListStore
    if (store.selectedInstancesIds.length === store.filteredCollection.length) {
      store.unselectAll()
    } else {
      store.selectAll()
    }
  }

  protected renderSecondRow(): JSX.Element {
    const { openFormTypeSelectionForm, isCreationAvailable } =
      this.props.logisticsStore

    return (
      <div className="tw:flex tw:items-center tw:relative tw:pb-2.5">
        <div className="tw:flex-none no-flex-children-direct tw:ml-[15px]">
          {this.renderSearchFilter()}
        </div>
        <div className="separator tw:mx-2.5 tw:grow-0" />

        {this.renderSortingOptions()}
        {this.renderGroupingOptions()}
        <div className="tw:flex tw:items-center tw:gap-2.5 no-flex">
          {this.renderMyEntitiesButton()}
          {this.renderMyCompanyButton()}
        </div>
        {this.renderStateFilter()}
        {this.isColumnsConfigurationAvailable && isCreationAvailable && (
          <div className="tw:grow-0 tw:relative">
            <LogisticsCreationListMenu
              openFormTypeSelectionForm={openFormTypeSelectionForm}
              className="tw:ml-[15px]"
            />
          </div>
        )}
      </div>
    )
  }

  private renderActionButton = ({
    actionType,
    icon,
    isShown,
  }: IAction): JSX.Element => {
    if (!isShown(this.isAdminUser)) {
      return null
    }

    const { eventsStore } = this.props

    const isBtnEnabled = this.isActionEnabled(actionType)
    const actionTooltip = this.getActionTooltip(isBtnEnabled, actionType)

    if (isUpdateStatusAction(actionType)) {
      return (
        <React.Fragment key={actionType}>
          <ConfirmBatchStatusUpdateDialog
            logisticsListStore={this.selectedLogisticsListStore}
            eventsStore={eventsStore}
          />
          <TooltipWrapper
            key={actionType}
            className="tw:grow-0"
            content={actionTooltip}
            disabled={!actionTooltip}
          >
            <PopoverComponent
              title={Localization.translator.updateSelectedWorkflowsStatusTo}
              content={close => (
                <StatusUpdateMenu
                  logisticsListStore={this.selectedLogisticsListStore}
                  onStatusSelected={this.onStatusSelected}
                  onClose={close}
                />
              )}
            >
              <Button
                variant="ghost"
                disabled={!isBtnEnabled}
                onClick={this.setActionType.bind(null, actionType)}
                data-testid="logistics-update-status-button"
              >
                {icon}
                {getLogisticActionTypeDisplayName(actionType)}
              </Button>
            </PopoverComponent>
          </TooltipWrapper>
        </React.Fragment>
      )
    }

    if (isSubscribeAction(actionType)) {
      const { getSelectedNonDraftPermits } = this.selectedLogisticsListStore
      return (
        <React.Fragment key={actionType}>
          <TooltipWrapper
            key={actionType}
            className="tw:grow-0"
            content={actionTooltip}
            disabled={!actionTooltip}
          >
            <PopoverComponent
              title={Localization.translator.subscribeSelected}
              content={close => (
                <SubscribeUsersSelector
                  selectedWorkflows={getSelectedNonDraftPermits}
                  onClose={close}
                />
              )}
            >
              <Button
                variant="ghost"
                disabled={!isBtnEnabled}
                data-testid="logistics-subscribe-button"
              >
                {icon}
                {getLogisticActionTypeDisplayName(actionType)}
              </Button>
            </PopoverComponent>
          </TooltipWrapper>
        </React.Fragment>
      )
    }

    const testIdMap = {
      [LogisticActionTypes.DUPLICATE]: 'logistics-duplicate-button',
      [LogisticActionTypes.DELETE]: 'logistics-delete-button',
    }

    return (
      <TooltipWrapper
        key={actionType}
        className="tw:grow-0"
        content={actionTooltip}
        disabled={!actionTooltip}
      >
        <Button
          variant="ghost"
          disabled={!isBtnEnabled}
          onClick={this.setActionType.bind(null, actionType)}
          data-testid={testIdMap[actionType]}
        >
          {icon}
          {getLogisticActionTypeDisplayName(actionType)}
        </Button>
      </TooltipWrapper>
    )
  }

  private onStatusSelected = (item: IStatusItem) => {
    this.setActionType(LogisticActionTypes.UPDATE_STATUS)
    const store = this.selectedLogisticsListStore
    store.openBatchStatusUpdateConfirmModal(item)
  }

  private setActionType = (actionType: LogisticActionTypes) => {
    const store = this.selectedLogisticsListStore
    store.setActionType(actionType)
  }

  private isActionEnabled = (actionType: LogisticActionTypes): boolean => {
    const store = this.selectedLogisticsListStore

    if (actionType === LogisticActionTypes.DUPLICATE) {
      return store.selectedInstancesIds.length === 1
    }

    return !isDeleteAction(actionType)
      ? !store.areSelectedWorkflowsDraft
      : !store.areSelectedWorkflowsDone
  }

  private get selectedLogisticsListStore():
    | LogisticsListStore
    | LogisticsGanttStore {
    return this.store.isGanttViewMode
      ? this.props.logisticsGanttStore
      : this.props.logisticsListStore
  }

  private getActionTooltip = (
    isEnabled: boolean,
    actionType: LogisticActionTypes,
  ): string => {
    if (isEnabled) {
      return null
    }
    if (isDeleteAction(actionType)) {
      return Localization.translator.cannotDeleteDoneForms
    }
    if (isDuplicateAction(actionType)) {
      return Localization.translator.selectASingleWorkflowToDuplicate
    }

    return Localization.translator.unavailableDraftWorkflowsSelected
  }

  private renderStateFilter() {
    const { logisticsForceCloseMap, viewMode } = this.store
    const { eventsStore } = this.props

    return (
      <LogisticsFilter
        store={this.logisticsFilterStore}
        forceCloseMap={logisticsForceCloseMap}
        dynamicOverflowListStore={this.dynamicOverflowListStore}
        eventsStore={eventsStore}
        filters={this.appState.workflowsFilters}
        viewMode={viewMode}
        onResetClick={() => this.workflowSavedViewsRef.current?.reset()}
      />
    )
  }

  private getIconByFilter(filterType: string) {
    return FILTER_TYPE_ICONS[filterType]
  }

  private renderDatePicker() {
    const {
      projectsStore,
      projectDateStore,
      logisticsStore: { dateFilters, selectedDatePickerMode },
    } = this.props
    const {
      datePickerStates,
      forceDatePickerClose,
      onShowDatePickerChanged,
      getDatePickerModeDisplayName,
    } = this.store

    return (
      <DateSelector
        filters={dateFilters}
        forceClose={forceDatePickerClose}
        shouldUseRelativePosition={false}
        onShowChanged={onShowDatePickerChanged}
        mode={selectedDatePickerMode}
        projectsStore={projectsStore}
        projectDateStore={projectDateStore}
        shouldUseOtherDatesForFullProjectMode
        datePickerModes={datePickerStates}
        onOptionModeClick={this.changeDatePickerMode}
        shouldShowTodayButton
        shouldRenderDateModes
        shouldHideTopButtons
        getCustomModeDisplayName={getDatePickerModeDisplayName}
        isAlternativeArrows
        isAlternativeButton
      />
    )
  }

  private renderReportButton = () => {
    if (!this.store.isListViewMode) {
      return
    }

    const { logisticsStore, logisticsListStore } = this.localProps
    const { additionalReportFiles, isAnyFileTypeChecked } = this.store

    return (
      <PermitReportButton
        title={Localization.translator.createReport}
        className="tw:ml-[15px]"
        logisticsStore={logisticsStore}
        logisticsListStore={logisticsListStore}
        shouldHideIcon={true}
        toggleModal={this.toggleModal}
        shouldInvertColors={true}
        getLocationFilterKeySummary={this.getLocationFilterKeySummary}
        additionalReportFiles={additionalReportFiles}
        isEnabled={isAnyFileTypeChecked}
      />
    )
  }

  private changeDatePickerMode = (mode: TwoMonthsDatePickerMode) => {
    this.props.logisticsStore.changeDatePickerMode(mode)
    this.props.logisticsGanttStore.startOrStopTimeTrackingIfNeeded()
    this.dynamicOverflowListStore.enableRecalculation()
  }

  private get appState(): DesktopInitialState {
    return this.props.eventsStore.appState
  }

  private getIconBySpecificFilter(filterType: string) {
    const Icon = locationTypeToTagIconMap[filterType as LocationType]

    return (
      Icon && (
        <Icon
          className="tw:grow-0 tw:flex tw:items-center tw:mr-2.5"
          size={ICON_SIZE}
        />
      )
    )
  }

  private handleSpecificFilterType = (
    filterType: string,
    baseIndex: number,
  ) => {
    const { filterStoresByTypeMap } = this.logisticsFilterStore
    const { filter } = filterStoresByTypeMap[filterType]
    const result: { [filterType: string]: string[] } = {}

    const keys: string[] = Array.from(filter.selectedFilterOptions.keys())
    keys.forEach(key => {
      const { type, name: itemName } = this.getLocationFilterKeySummary(key)

      if (!itemName) return

      if (result[type]) {
        result[type].push(itemName)
      } else {
        result[type] = [itemName]
      }
    })

    return Object.keys(result).map((resultFilter, index) => {
      const icon = this.getIconBySpecificFilter(resultFilter)
      return (
        <div key={resultFilter} className="tw:flex tw:items-center">
          <div className="text large orange tw:mr-5 tw:grow-0">
            {`${baseIndex + index + 1}.`}
          </div>
          <div className="filters-row tw:flex tw:items-center">
            {icon}
            {getLocationTypeDisplayName(resultFilter as LocationType) ??
              Localization.translator.location}
          </div>
          <div className="tw:justify-end tw:flex tw:items-center filters-date-details tw:tracking-[1.5px] end tw:tracking-[1.5px] tw:flex tw:items-center text line-26 filters-row">
            {result[resultFilter].join(', ')}
          </div>
        </div>
      )
    })
  }

  private getLocationFilterKeySummary = (
    key: string,
  ): ILocationFilterKeySummary =>
    getLocationFilterKeySummary(
      key,
      this.props.locationAttributesStore.getByIdWithDeleted,
    )

  private onGroupingChanged = () => {
    this.dynamicOverflowListStore.enableRecalculation()
    this.props.logisticsListStore.resetCollapsing()
    this.props.logisticsGanttStore.resetCollapsing()
  }

  private get isColumnsConfigurationAvailable(): boolean {
    return this.store.isListViewMode || this.store.isGanttViewMode
  }
}
